
import { ref, computed, onMounted, onBeforeUnmount, watch, getCurrentInstance, nextTick, defineComponent } from 'vue';
import InfoBox from '@/components/InfoBox.vue';

import Modal from '@/components/ModalAliare.vue';
import ModalButtonSuccess from '@/components/ModalButtonSuccess.vue';
import ModalButtonCancel from '@/components/ModalButtonCancel.vue';
import AliareSelect from '@/components/AliareSelect.vue';
import AliareInput from '@/components/AliareInput.vue';
import DuplicateProject from '@/components/ModalDuplicateProject.vue';
import AlertNotificationModal from '@/components/AlertNotificationModal.vue';

import WebStorage from '@/common/WebStorage';
import { useProjectPermission } from '@/composables/useProjectPermission';
import { useGenericHelpers } from '@/composables/useGenericHelpers';

export default defineComponent({
	name: 'CardFlow',
	components: {
		Modal,
		ModalButtonSuccess,
		ModalButtonCancel,
		DuplicateProject,
		AliareSelect,
		AliareInput,
		InfoBox,
		AlertNotificationModal,
	},
	props: {
		arrayCardData: {
			type: Array as () => any[],
			default: () => [],
		},
	},
	setup(props, { emit }) {
		const instance = getCurrentInstance();
		const proxy = instance?.proxy as any;
		const store = proxy.$store;
		const router = proxy.$router;
		const root = proxy.$root;

		// Composables
		const projectPermission = useProjectPermission();
		const genericHelpers = useGenericHelpers();

		// Data
		const deleteData = ref<any>({});
		const showAlert = ref(false);
		const editAux = ref<any[]>([]);
		const show = ref(false);
		const message = ref('');
		const activeBtnEdit = ref(false);
		const selected = ref('');
		const selectedAccountData = ref('');
		const organization = ref<any[]>([]);
		const isLoadingAction = ref(false);
		const alert = ref('projectDetailing.alert');
		const success = ref('projectDetailing.success');
		const error = ref('projectDetailing.failure');
		const titleBody = ref('project.selectResultsLabel');
		const activeStatus = ref('activeDeactivateButton.active');
		const inactiveStatus = ref('activeDeactivateButton.inactive');
		const textCreateFlow = ref('projectDetailing.btn_1');
		const textDuplicateProject = ref('project.tect_2');
		const textDeleteProject = ref('project.textDeleteProject');
		const viewOption = ref(false);
		const tokenData = ref('');
		const isLoading = ref(false);
		const selectMode = ref(false);
		const arrayProjectsSelected = ref<any[]>([]);
		const renderComponent = ref(true);

		// Computed
		const workspace = computed(() => store.getters.workspace);
		
		const projectsAbleToSelect = computed(() => {
			return props.arrayCardData.filter((project: any) => project.permissao == 'Editar').length;
		});

		const allProjectsSelected = computed(() => {
			return projectsAbleToSelect.value == arrayProjectsSelected.value.length;
		});

		const partialProjectsSelected = computed(() => {
			return projectsAbleToSelect.value != arrayProjectsSelected.value.length && arrayProjectsSelected.value.length > 0;
		});

		const deleteProjectAction = (payload: any) => store.dispatch('project/delete', payload);
		const patchActive = (payload: any) => store.dispatch('project/patchActive', payload);

		// Methods
		const emitSelectAll = () => {
			isLoading.value = true;
			if (allProjectsSelected.value) {
				props.arrayCardData.forEach((form: any) => {
					form.selectedProject = false;
				});
				arrayProjectsSelected.value = [];
				isLoading.value = false;
				root.$emit('deselectAll');
			} else {
				emit('selectAllProjects');
			}
		};

		const forceRerender = async () => {
			renderComponent.value = false;
			await nextTick();
			renderComponent.value = true;
		};

		const toggleSelectProject = (form: any, emitSwitch?: boolean) => {
			if (form.permissao != 'Editar') return;

			root.$emit('addOrRemoveItemSelected', JSON.parse(JSON.stringify({
				...form,
				canShare: projectPermission.canShareProject(form),
			})));
			form.selectedProject = !form?.selectedProject;

			if (form.selectedProject) {
				arrayProjectsSelected.value.push(form);
			} else {
				let index = arrayProjectsSelected.value.map(items => {
					return items.id;
				}).indexOf(form.id);

				arrayProjectsSelected.value.splice(index, 1);
			}
			if (emitSwitch) {
				root.$emit('switchSelectMode', {active: JSON.parse(JSON.stringify(form.selectedProject)), object: 'Projeto'});
			}
			forceRerender();
		};

		const openModalAlert = async () => {
			showAlert.value = true;
			proxy.$analytics.resourceManaged('Projeto', 'Deletar projeto');
		};

		const newWorkflow = (id: string) => {
			proxy.$analytics.resourceManaged('Projetos', 'Adicionar Fluxo');
			router.push({ name: 'NodeViewNew', params: { projectid: id } });
			proxy.$analytics.resourceManaged('Projeto', 'Criar fluxo');
		};

		const acessDetails = (form: any) => {
			if (selectMode.value) {
				toggleSelectProject(form);
				return;
			}
			proxy.$analytics.resourceManaged('Projetos', 'Acessar Detalhamento');
			router.push({
				name: 'projectDetailing',
				params: { id: form.id },
			});
			proxy.$analytics.resourceManaged('Projeto', 'Acessar projeto');
		};

		const onSelect = (type: string, dataAccount: any) => {
			selected.value = type;
			selectedAccountData.value = dataAccount;
		};

		const oneOptionSelected = (payload: any, dataAccount: any) => {
			selected.value = payload;
			selectedAccountData.value = dataAccount;
		};

		const deleteProject = async (deleteDataParam: any) => {
			const deleteProjectName = deleteDataParam.form.nome;
			proxy.$analytics.resourceManaged('Projetos', 'Excluir');
			isLoadingAction.value = true;
			await deleteProjectAction({workspaceId: workspace.value.id, projetoId: deleteDataParam.form.id})
				.then((aux: any) => {
					showAlert.value = false;
					store.commit('activeAlert', {
						message: proxy.$locale.baseText('project.modalDelete.projectDeleteSuccess', { interpolate: {deleteProjectName} }),
					});

					const removed = props.arrayCardData.splice(deleteDataParam.index, 1);
					projectPermission.validationAccessProject();
				})
				.catch((error: any) => {
					const deleteError = error.response?.data?.notifications;
					store.commit('activeAlert', {
						message: proxy.$locale.baseText('project.modalDelete.deleteError', { interpolate: {deleteError} }),
						status: 'error',
					});
				});
			isLoadingAction.value = false;
		};

		const changeActiveStatus = async (form: any, index: number) => {
			try {
				proxy.$analytics.resourceManaged('Projetos', form.ativo ? 'Inativar' : 'Ativar');
				const payload = { workspaceId: workspace.value.id, projetoId: form.id, ativo: !form.ativo };
				const result = await patchActive(payload);

				form.ativo = result.data.ativo;
				const ProjectName = form.nome;

				store.commit('activeAlert', {
					message: proxy.$locale.baseText(result.data.ativo ? 'project.sucessActive' : 'project.sucessInactive', { interpolate: { ProjectName } }),
				});

				emit('reload');
				projectPermission.validationAccessProject();
				proxy.$analytics.resourceManaged('Projeto', 'Ativar/Inativar');
				return form.ativo;
			} catch (err) {
				store.commit('activeAlert', {
					message: proxy.$locale.baseText('project.errorActiveOrInactive', { interpolate: { ErrorStatus: err } }),
					status: 'error',
				});

				return form.ativo;
			}
		};

		const showModals = (form: any, key: string) => {
			if (key === 'createEditProject') {
				proxy.$analytics.resourceManaged('Projetos', 'Abrir Modal de Edição');
				store.dispatch('ui/openCreateEditProjectModal', { data: form, mode: 'edit' });
				proxy.$analytics.resourceManaged('Projeto', 'Renomear projeto');
			}
		};

		const openModalDuplicateProject = (projectId: string, multi: boolean) => {
			proxy.$analytics.resourceManaged('Projetos', 'Duplicar');
			const openMode = !multi ? 'oneAccount' : 'multiAccount';
			store.dispatch('ui/openDuplicateProjectModal', { data: { projectId }, mode: openMode });
			proxy.$analytics.resourceManaged('Projeto', 'Duplicar projeto');
		};

		const iconChange = (change: any) => {
			if (change.tipoObjeto === 'Projeto') {
				change.objetoId.forEach((id: string) => {
					const card = props.arrayCardData?.find((card: any) => card.id === id);
					if (card) {
						card.compartilhado = change?.deleteShare ? !card.compartilhado : true;
					}
				});
			}
		};

		const handleSwitchSelectMode = () => {
			selectMode.value = !selectMode.value;
		};

		const handleCancelSwitchMode = () => {
			props.arrayCardData.forEach((form: any) => {
				form.selectedProject = false;
			});
			arrayProjectsSelected.value = [];
			selectMode.value = false;
		};

		// Lifecycle hooks
		onMounted(() => {
			organization.value = JSON.parse(WebStorage.getItem('cardOrganization') || '[]');
			tokenData.value = WebStorage.getItem('tokenData') || '';

			root.$on('switchSelectMode', handleSwitchSelectMode);
			root.$on('cancelSwitchMode', handleCancelSwitchMode);
			root.$on('iconChange', iconChange);
		});

		onBeforeUnmount(() => {
			root.$off('switchSelectMode', handleSwitchSelectMode);
			root.$off('cancelSwitchMode', handleCancelSwitchMode);
			root.$off('iconChange', iconChange);
		});

		// Watch
		watch(
			() => props.arrayCardData,
			(newValue, oldValue) => {
				if (!newValue || !newValue.length) return;
				
				const projectsSelected = props.arrayCardData.filter((project: any) => project.selectedProject);
				if (projectsSelected.length == 0) {
					return;
				}
				arrayProjectsSelected.value = JSON.parse(JSON.stringify(projectsSelected));
				isLoading.value = false;
				root.$emit('selectAll', JSON.parse(JSON.stringify(projectsSelected)), {active: true, object: 'Projeto'});
			}
		);

		return {
			// Data
			deleteData,
			showAlert,
			editAux,
			show,
			message,
			activeBtnEdit,
			selected,
			selectedAccountData,
			organization,
			isLoadingAction,
			alert,
			success,
			error,
			titleBody,
			activeStatus,
			inactiveStatus,
			textCreateFlow,
			textDuplicateProject,
			textDeleteProject,
			viewOption,
			tokenData,
			isLoading,
			selectMode,
			arrayProjectsSelected,
			renderComponent,
			// Computed
			workspace,
			allProjectsSelected,
			projectsAbleToSelect,
			partialProjectsSelected,
			// Methods
			emitSelectAll,
			forceRerender,
			toggleSelectProject,
			openModalAlert,
			newWorkflow,
			acessDetails,
			onSelect,
			oneOptionSelected,
			deleteProject,
			changeActiveStatus,
			showModals,
			openModalDuplicateProject,
			iconChange,
			// From composables
			...projectPermission,
			...genericHelpers,
		};
	},
});
